import type { Step } from '@folio/types';
import { useCallback, useEffect, useState } from 'react';
import { generatePath, matchPath, useLocation, useNavigate, useParams } from 'react-router-dom';

// Helper function to normalize paths by removing trailing slashes
const normalizePath = (path: string): string => {
  return path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path;
};

// Helper function to get visible steps (filter out hidden ones)
const getVisibleSteps = (steps: Step[]): Step[] => {
  return steps.filter(step => !step.hidden);
};

export const useKorintStepper = (steps: Step[], basePath = '', afterEndPath = '') => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();

  // Normalize the current pathname
  const normalizedPathname = normalizePath(location.pathname);

  // biome-ignore lint/style/useTemplate: Use this style for TS
  const match = matchPath(basePath + '/:stepId', normalizedPathname);
  const stepId = match?.params.stepId;
  const subStepId = match?.params.subStepId;

  const currentStepIndex = steps.findIndex(step => {
    const generatedPath = generatePath(`${basePath}/${step.path}`, params);
    const normalizedGeneratedPath = normalizePath(generatedPath);
    return normalizedGeneratedPath === normalizedPathname;
  });

  // Get visible steps for navigation logic
  const visibleSteps = getVisibleSteps(steps);

  // Find the index of current step in visible steps
  const currentStep = steps[currentStepIndex];
  const visualActiveStepIndex = currentStep
    ? visibleSteps.findIndex(step => step.path === currentStep.path)
    : -1;

  const isLastStep = visualActiveStepIndex === visibleSteps.length - 1;

  const isFirstStep = visualActiveStepIndex === 0;

  const [maxVisitedVisibleIndex, setMaxVisitedVisibleIndex] = useState(visualActiveStepIndex);

  useEffect(() => {
    if (visualActiveStepIndex > maxVisitedVisibleIndex) {
      setMaxVisitedVisibleIndex(visualActiveStepIndex);
    }
  }, [visualActiveStepIndex, maxVisitedVisibleIndex]);

  const invalidateFutureSteps = useCallback(() => {
    setMaxVisitedVisibleIndex(visualActiveStepIndex);
  }, [visualActiveStepIndex]);

  const replaceParams = (replaceParams: Record<string, string>, replaceHistory?: boolean) => {
    const realBasePath = generatePath(basePath, {
      ...params,
      ...replaceParams,
    });

    // Ensure consistent path construction without double slashes
    const targetPath = `${realBasePath}/${stepId}${subStepId ? `/${subStepId}` : ''}`;
    navigate(normalizePath(targetPath), {
      replace: replaceHistory,
    });
  };

  const next = (replaceParams?: Record<string, string>) => {
    if (currentStepIndex < steps.length - 1) {
      const targetPath = generatePath(`${basePath}/${steps[currentStepIndex + 1].path}`, {
        ...params,
        ...replaceParams,
      });
      return navigate(normalizePath(targetPath));
    }
    if (afterEndPath) {
      return navigate(generatePath(afterEndPath, { ...params, ...replaceParams }));
    }
  };

  const previous = (replaceParams?: Record<string, string>) => {
    if (currentStepIndex === 0) return;

    let previousStepIndex = currentStepIndex - 1;
    while (steps[previousStepIndex].hidden && previousStepIndex > 0) {
      previousStepIndex = previousStepIndex - 1;
    }

    const targetPath = generatePath(`${basePath}/${steps[previousStepIndex].path}`, {
      ...params,
      ...replaceParams,
    });
    navigate(normalizePath(targetPath), { state: { navigationDirection: 'back' } });
  };

  const handleClick = (i: number, search?: string) => {
    const realBasePath = generatePath(basePath, params);
    const targetPath = `${realBasePath}/${steps[i].path}`;
    navigate({ pathname: normalizePath(targetPath), search });
  };

  return {
    next,
    previous,
    activeStepIndex: currentStepIndex,
    visualActiveStepIndex,
    maxVisitedVisibleIndex,
    visibleSteps,
    handleClick,
    isLastStep,
    isFirstStep,
    replaceParams,
    invalidateFutureSteps,
  };
};
